import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { customerAPI } from '../services/api';
import api from '../services/api';
import { ArrowLeft } from 'lucide-react';

const CustomerForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEdit = !!id;

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  // Fetch customer settings to get dynamic status and area options
  const { data: customerSettings, isLoading: settingsLoading, isError: settingsError } = useQuery('customer-settings', async () => {
    const res = await api.get('/settings/customers');
    return res.data.data;
  }, {
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { isLoading, data: customerData } = useQuery(
    ['customer', id],
    () => customerAPI.getOne(id).then(res => res.data.data),
    {
      enabled: isEdit,
      staleTime: 2 * 60 * 1000 // Keep data fresh for 2 minutes
    }
  );

  // Reset form when customer data loads (using useEffect would be better but this works)
  React.useEffect(() => {
    if (customerData && isEdit) {
      // Format dates for input fields
      const formData = {
        ...customerData,
        requestDate: customerData.requestDate || '',
        installationDate: customerData.installationDate || ''
      };
      reset(formData);
    }
  }, [customerData, isEdit, reset]);

  const statusOptions = customerSettings?.statusOptions || [
    'Not Processed', 'Pending Installation', 'In Process', 'Done', 'Canceled',
    'Future Fiber', 'Standby', 'Reminder', 'Suspended', 'Temporary Disconnected'
  ];

  // Include customer's current area in options if it's not already there
  let areaOptions = customerSettings?.areaOptions || [];
  if (isEdit && customerData?.area && !areaOptions.includes(customerData.area)) {
    areaOptions = [...areaOptions, customerData.area];
  }

  const mutation = useMutation(
    (data) => isEdit ? customerAPI.update(id, data) : customerAPI.create(data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('customers');
        queryClient.invalidateQueries('statistics');
        queryClient.invalidateQueries('customersWithNotes');
        if (isEdit) {
          queryClient.invalidateQueries(['customer', id]);
        }
        toast.success(`Customer ${isEdit ? 'updated' : 'created'} successfully`);
        if (isEdit) {
          // Stay on the same page when editing
        } else {
          // Navigate to edit page of newly created customer
          const newId = response?.data?.id;
          navigate(newId ? `/customers/${newId}/edit` : '/customers');
        }
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.message ||
                        error.response?.data?.errors?.[0]?.message ||
                        error.response?.data?.error ||
                        'Operation failed';
        toast.error(errorMsg);
        console.error('Customer save error:', error.response?.data);
      }
    }
  );

  const onSubmit = (data) => {
    // Remove read-only fields that shouldn't be sent to the API
    const { id, createdAt, updatedAt, statusHistory, ...rawData } = data;

    // Clean data - allow clearing fields when editing
    const cleanData = {};
    for (const [key, value] of Object.entries(rawData)) {
      if (value === '' || value === undefined || value === null) {
        // When editing, send null so cleared fields are persisted
        if (isEdit && key !== 'customerName') {
          cleanData[key] = null;
        }
        continue;
      }
      cleanData[key] = value;
    }

    // Validate GPS coordinates before submitting
    if (cleanData.pinLocation) {
      const parts = cleanData.pinLocation.split(',').map(s => s.trim());
      if (parts.length !== 2) {
        toast.error('GPS coordinates must be in format: latitude, longitude (e.g. 34.755711, 32.423634)');
        return;
      }
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (isNaN(lat) || isNaN(lng)) {
        toast.error('GPS coordinates must be valid numbers');
        return;
      }
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        toast.error('Invalid GPS coordinates. Latitude must be -90 to 90, longitude -180 to 180. Check for missing decimal point.');
        return;
      }
    }

    mutation.mutate(cleanData);
  };

  if (isLoading || (isEdit && !customerData)) return <div>Loading...</div>;
  // Settings are optional - form will use fallback values if not loaded

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="btn btn-secondary w-fit">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-3xl font-bold">{isEdit ? 'Edit' : 'New'} Customer</h1>
      </div>

      {(settingsError || (!customerSettings && !settingsLoading)) && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
          Could not load custom settings. Using default options.
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-8">
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-primary-600">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Customer Name</label>
              <input {...register('customerName')} className="input" />
            </div>
            <div>
              <label className="label">Phone</label>
              <input {...register('phone')} className="input" placeholder="99123456" />
            </div>
            <div>
              <label className="label">Email</label>
              <input type="email" {...register('email')} className="input" placeholder="customer@example.com" />
            </div>
            <div>
              <label className="label">Area</label>
              <select {...register('area')} className="input">
                <option value="">Select Area</option>
                {areaOptions.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Address & Location */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-primary-600">Address & Location</h2>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="label">Full Address</label>
              <textarea {...register('fullAddress')} className="input" rows={2} placeholder="Street name, number, city" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">GPS Coordinates (Pin Location)</label>
                <input {...register('pinLocation')} className="input" placeholder="34.755711, 32.423634" />
              </div>
              <div>
                <label className="label">Status</label>
                <select {...register('status')} className="input">
                  <option value="">Select Status</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* CYTA Fiber Details */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-primary-600">CYTA Fiber Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">CYTA Fiber Code</label>
              <input {...register('cytaFiberCode')} className="input" placeholder="FIBERAF-FTTH-AF-EA488491-BFY02D1AB 42 (D)" />
            </div>
            <div>
              <label className="label">CYTA Reference Number</label>
              <input {...register('cytaReference')} className="input" placeholder="PM202304718" />
            </div>
            <div>
              <label className="label">CYTA Circuit Number</label>
              <input {...register('cytaCircuitNumber')} className="input" placeholder="40918198" />
            </div>
            <div>
              <label className="label">GPON Serial</label>
              <input {...register('gponSerial')} className="input" placeholder="48575443102044983" />
            </div>
            <div>
              <label className="label">CYTA Plan</label>
              <select {...register('cytaPlan')} className="input">
                <option value="">Select Plan</option>
                <option value="#112 200MBPS">#112 200MBPS</option>
                <option value="#112 500MBPS">#112 500MBPS</option>
                <option value="#112 1000MBPS">#112 1000MBPS</option>
              </select>
            </div>
            <div>
              <label className="label">Request Date</label>
              <input type="date" {...register('requestDate')} className="input" />
            </div>
            <div>
              <label className="label">Installation Date</label>
              <input type="date" {...register('installationDate')} className="input" />
            </div>
          </div>
        </div>

        {/* Remarks */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-primary-600">Additional Notes</h2>
          <div>
            <label className="label">Remarks / Notes</label>
            <textarea {...register('remarks')} className="input" rows={3} placeholder="Any additional information or notes..." />
          </div>
        </div>

        <div className="flex gap-4 pt-4">
          <button type="submit" className="btn btn-primary" disabled={mutation.isLoading}>
            {mutation.isLoading ? 'Saving...' : 'Save Customer'}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;
